import { useEffect, useMemo } from 'react';
import { BatchValueEntry } from '../../../components/prioritization/sessions/ItemValueEntry';
import { ScaleCalibrationCard } from '../../../components/prioritization/session/ScaleCalibrationCard';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { C } from '../theme/participantTokens';
import { isValueSession } from '../utils/sessionHelpers';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';

const SESSION_QUESTIONS: Record<string, { question: string; subtext: string }> = {
  select_attributes: {
    question: 'Which criteria should matter most?',
    subtext: 'Select evaluation criteria relevant for comparing these items.',
  },
  select_item_pool: {
    question: 'What should be in scope?',
    subtext: 'Curate the item pool — add items that belong and remove those that do not fit.',
  },
  converge_values: {
    question: 'What are the values for each item?',
    subtext: 'Enter values you know for each item on each criterion.',
  },
  calibration: {
    question: 'Help align the team on scoring',
    subtext: 'Participate in calibration so everyone applies the same standards.',
  },
  review: {
    question: 'Review these items',
    subtext: 'Provide your comparative assessment — no strict ranking required.',
  },
};

interface ConfigContributorParticipantProps {
  session: PrioritizationSession;
  onBack: () => void;
}

export function ConfigContributorParticipant({ session, onBack }: ConfigContributorParticipantProps) {
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? 'Participant';
  const q = SESSION_QUESTIONS[session.intent ?? ''] ?? {
    question: 'Complete your contribution',
    subtext: 'Follow the instructions below to complete your input.',
  };

  // S2 (item pool) sessions: show the live pool instead of a dead end. The
  // list updates as the facilitator curates, so participants follow along and
  // discuss — the pool IS the shared artifact of this step.
  const isPoolSession = session.intent === 'select_item_pool';
  const items = useItemsStore(s => s.items);
  const fetchItems = useItemsStore(s => s.fetchFromSupabase);
  useEffect(() => {
    if (isPoolSession && items.length === 0) void fetchItems();
  }, [isPoolSession, items.length, fetchItems]);
  const poolItems = useMemo(
    () => (isPoolSession ? items.filter(i => session.itemIds?.includes(i.id)) : []),
    [isPoolSession, items, session.itemIds],
  );

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <LayerBar>
        <LayerBack label="Brief" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec }}>
          {session.name}
        </div>
      </LayerBar>
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px 48px', maxWidth: 860 }}>
        <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderLeft: `3px solid ${C.slate}`, borderRadius: 4, padding: '20px 22px', marginBottom: 16 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.textDim, marginBottom: 6 }}>
            Configuration step
          </div>
          <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.2rem', fontWeight: 600, color: C.textPri, marginBottom: 8 }}>
            {q.question}
          </div>
          <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, margin: 0 }}>
            {q.subtext}
          </p>
        </div>

        <div className="pp-tool-embed" style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: 16 }}>
          {isValueSession(session) && <BatchValueEntry session={session} />}
          {session.intent === 'calibration' && (
            <ScaleCalibrationCard
              sessionId={session.id}
              participantId={userId}
              participantName={userName}
            />
          )}
          {isPoolSession && (
            <div>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: C.textDim, marginBottom: 10 }}>
                Current pool · {poolItems.length} item{poolItems.length !== 1 ? 's' : ''}
              </div>
              {poolItems.length === 0 ? (
                <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', color: C.textTer, margin: 0 }}>
                  The pool is empty so far — items appear here live as your facilitator adds them.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {poolItems.map(item => (
                    <li
                      key={item.id}
                      style={{ padding: '10px 12px', background: C.bg, border: `1px solid ${C.rule}`, borderRadius: 4, fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.88rem', color: C.textPri }}
                    >
                      {item.title}
                      {item.description && (
                        <div style={{ fontSize: '0.78rem', color: C.textTer, marginTop: 2 }}>{item.description}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.78rem', color: C.textDim, marginTop: 12, marginBottom: 0 }}>
                Scope decisions are made together in this session — speak up about anything that should be added or cut. Your facilitator applies the changes and this list updates live.
              </p>
            </div>
          )}
          {!isValueSession(session) && session.intent !== 'calibration' && !isPoolSession && (
            <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', color: C.textTer }}>
              Use the session configuration tools in the facilitator view to complete pool, criteria, and participation selections.
              Your facilitator will guide this step.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
